import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import { Plus, Syringe, Bell, X, FileText, Stethoscope, BedDouble, MapPin, Sparkles, Printer, Radio, Heart } from "lucide-react";
import { track } from "../lib/analytics";

const SPECIES = ["dog", "cat", "bird", "rabbit", "other"];

export default function MyPets() {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", species: "dog", breed: "", age_years: 1 });
  const [saving, setSaving] = useState(false);
  const [adoptConfirm, setAdoptConfirm] = useState(null); // pet object

  const load = () => axios.get(`${API}/pets`).then(r => setPets(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/pets`, { ...form, age_years: Number(form.age_years) });
      track("pet_added", { species: form.species });
      setOpen(false);
      setForm({ name: "", species: "dog", breed: "", age_years: 1 });
      await load();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="mypets-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-extrabold">My Pets</h1>
        <button
          data-testid="add-pet-button"
          onClick={() => setOpen(true)}
          className="bg-brand text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover"
        >
          <Plus className="w-4 h-4" /> Add pet
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {pets.map(p => (
          <article key={p.pet_id} data-testid={`pet-card-${p.pet_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
            <div className="relative h-44">
              <img src={p.photo} alt={p.name} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 bg-surface/95 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">{p.species}</div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading text-2xl font-extrabold">{p.name}</h3>
                  <div className="text-sm text-ink-soft">{p.breed} · {p.age_years} years</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/app/pets/${p.pet_id}/collar`}
                    data-testid={`pet-collar-${p.pet_id}`}
                    className="bg-leaf/40 text-ink rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-leaf/60 whitespace-nowrap"
                    title={p.collar_config?.enabled ? "Manage GPS collar" : "Connect GPS collar"}
                  >
                    <Radio className="w-3.5 h-3.5"/> {p.collar_config?.enabled ? "Collar ✓" : "Collar"}
                  </Link>
                  <Link
                    to={`/app/pets/${p.pet_id}/poster`}
                    data-testid={`pet-poster-${p.pet_id}`}
                    className="bg-brand-blue/10 text-brand-blue rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-brand-blue/20 whitespace-nowrap"
                    title="Print 'If found' poster"
                  >
                    <Printer className="w-3.5 h-3.5"/> Poster
                  </Link>
                  <Link
                    to={`/app/pets/${p.pet_id}/medical-history`}
                    data-testid={`view-medical-history-${p.pet_id}`}
                    className="bg-brand/10 text-brand rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-brand/20 whitespace-nowrap"
                  >
                    <FileText className="w-3.5 h-3.5"/> Medical
                  </Link>
                </div>
              </div>

              {p.vaccinations?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2 flex items-center gap-1"><Syringe className="w-3 h-3" /> Vaccinations</div>
                  <ul className="space-y-1.5 text-sm">
                    {p.vaccinations.map((v, i) => (
                      <li key={i} className="flex items-center justify-between bg-surface-muted rounded-xl px-3 py-2">
                        <span className="font-bold">{v.name}</span>
                        <span className="text-ink-soft text-xs">Next: {v.next_due}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {p.reminders?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2 flex items-center gap-1"><Bell className="w-3 h-3" /> Reminders</div>
                  <ul className="space-y-1.5 text-sm">
                    {p.reminders.map((v, i) => (
                      <li key={i} className="flex items-center justify-between bg-sunshine/20 rounded-xl px-3 py-2">
                        <span className="font-bold">{v.title}</span>
                        <span className="text-ink-soft text-xs">{v.due}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Interconnected quick-actions */}
              <div className="mt-4 grid grid-cols-2 gap-2" data-testid={`pet-actions-${p.pet_id}`}>
                <Link to="/app/vets" state={{ petId: p.pet_id }} className="bg-brand-blue/10 text-brand-blue rounded-2xl px-3 py-2 text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-brand-blue/20" data-testid={`book-vet-${p.pet_id}`}>
                  <Stethoscope className="w-3.5 h-3.5"/> Book vet
                </Link>
                <Link to="/app/hostels" state={{ petId: p.pet_id }} className="bg-brand-green/10 text-brand-green rounded-2xl px-3 py-2 text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-brand-green/20" data-testid={`book-hostel-${p.pet_id}`}>
                  <BedDouble className="w-3.5 h-3.5"/> Book hostel
                </Link>
                <Link to="/app/tracking" state={{ petId: p.pet_id }} className="bg-sunshine/30 text-ink rounded-2xl px-3 py-2 text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-sunshine/50" data-testid={`track-pet-${p.pet_id}`}>
                  <MapPin className="w-3.5 h-3.5"/> Track walk
                </Link>
                <Link to="/app/symptom-checker" state={{ petId: p.pet_id }} className="bg-brand/10 text-brand rounded-2xl px-3 py-2 text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-brand/20" data-testid={`symptom-pet-${p.pet_id}`}>
                  <Sparkles className="w-3.5 h-3.5"/> Symptom check
                </Link>
              </div>

              <button
                onClick={() => setAdoptConfirm(p)}
                data-testid={`give-for-adoption-${p.pet_id}`}
                className="mt-3 w-full bg-surface-muted hover:bg-brand/10 text-ink-soft hover:text-brand border border-line rounded-full px-3 py-2 text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors"
              >
                <Heart className="w-3.5 h-3.5"/> Give {p.name} for adoption
              </button>
            </div>
          </article>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={e => e.stopPropagation()}
            className="bg-surface rounded-3xl p-6 w-full max-w-md shadow-soft border border-line"
            data-testid="add-pet-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl font-extrabold">Add a pet</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <Field label="Name">
                <input data-testid="pet-name-input" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="input"/>
              </Field>
              <Field label="Species">
                <select data-testid="pet-species-input" value={form.species} onChange={e=>setForm({...form, species:e.target.value})} className="input">
                  {SPECIES.map(s => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Breed">
                <input data-testid="pet-breed-input" required value={form.breed} onChange={e=>setForm({...form, breed:e.target.value})} className="input"/>
              </Field>
              <Field label="Age (years)">
                <input data-testid="pet-age-input" type="number" min="0" step="0.1" required value={form.age_years} onChange={e=>setForm({...form, age_years:e.target.value})} className="input"/>
              </Field>
            </div>
            <button disabled={saving} data-testid="pet-save-button" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover disabled:opacity-60">
              {saving ? "Saving…" : "Save pet"}
            </button>
          </form>
        </div>
      )}

      {adoptConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setAdoptConfirm(null)} data-testid="give-adoption-confirm-dialog">
          <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-brand/15 grid place-items-center text-brand shrink-0">
                <Heart className="w-6 h-6"/>
              </div>
              <div className="flex-1">
                <h4 className="font-heading text-xl font-extrabold">Give {adoptConfirm.name} for adoption?</h4>
                <p className="text-sm text-ink-soft mt-1.5 leading-relaxed">
                  This will publish <span className="font-bold text-ink">{adoptConfirm.name}'s</span> profile publicly so other families can apply to adopt. We'll pre-fill the listing with {adoptConfirm.name}'s photo, breed and age — you can review and edit before publishing.
                </p>
                <p className="text-xs text-ink-soft mt-3 italic">Your phone and email stay private until you approve an applicant.</p>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setAdoptConfirm(null)}
                data-testid="give-adoption-cancel"
                className="flex-1 rounded-full border border-line py-2.5 text-sm font-bold hover:bg-surface-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  navigate("/app/adoption", { state: { prefillFromPet: adoptConfirm } });
                  setAdoptConfirm(null);
                }}
                data-testid="give-adoption-continue"
                className="flex-1 bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover"
              >
                Continue to listing
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.input:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
